import {
  createMemoryHistory,
  createRouter as _createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type Router,
} from 'vue-router'
import type { Pinia } from 'pinia'
import DashLayout from '@/components/layouts/DashLayout.vue'
import PublicLayout from '@/components/layouts/PublicLayout.vue'
import { useAuthStore } from '@/stores/authStore'

export function createRouter() {
  const routes: RouteRecordRaw[] = [
    {
      path: '/',
      component: PublicLayout,
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/public/LandingView.vue'),
        },
        {
          path: 'feature-requests',
          name: 'feature-requests',
          component: () => import('@/views/public/FeatureRequestsView.vue'),
        },
        {
          path: 'status',
          name: 'status',
          component: () => import('@/views/public/StatusView.vue'),
        },
        {
          path: 'support-docs',
          name: 'support-docs',
          component: () => import('@/views/public/SupportDocsView.vue'),
        },
        {
          path: 'contact-us',
          name: 'contact-us',
          component: () => import('@/views/public/ContactUsView.vue'),
        },
        {
          path: 'resources',
          name: 'resources',
          component: () => import('@/views/public/ResourcesView.vue'),
        },
        {
          path: 'resources/:slug',
          name: 'resource-detail',
          component: () => import('@/views/public/ResourceDetailView.vue'),
        },
        {
          path: 'integrations',
          name: 'integrations',
          component: () => import('@/views/public/IntegrationsView.vue'),
        },
        {
          path: 'pricing',
          name: 'pricing',
          component: () => import('@/views/public/PricingView.vue'),
        },
      ],
    },
    {
      path: '/auth/',
      component: () => import('@/components/layouts/AuthLayout.vue'),
      meta: { guest: true },
      children: [
        {
          path: '',
          name: 'login',
          component: () => import('@/views/auth/Login.vue'),
        },
        {
          path: 'signup',
          name: 'signup',
          component: () => import('@/views/auth/Register.vue'),
        },
        {
          path: 'verify-email',
          name: 'verify-email',
          meta: { registered: true, token : "" },
          component: () => import('@/views/auth/VerifyEmail.vue'),
        },
        {
          path: 'reset-password',
          name: 'reset-password',
          component: () => import('@/views/auth/ResetPassword.vue'),
        },
        {
          path: 'accept-invite',
          name: 'accept-invite',
          component: () => import('@/views/auth/AcceptInvite.vue'),
        },
      ],
    },
    {
      path: '/oauth/facebook/callback',
      // FACEBOOK_OAUTH_REDIRECT_URI in production points at the settings path; serve the
      // same page there too so a Meta redirect never lands on an unmatched (blank) route.
      alias: '/settings/connections/facebook/callback',
      name: 'facebook-oauth-callback',
      meta: { requiresAuth: true, title: 'Connecting Facebook' },
      component: () => import('@/views/oauth/FacebookCallbackView.vue'),
    },
    {
      path: '/approve/:token',
      name: 'public-approval',
      meta: { title: 'Review' },
      component: () => import('@/views/public/ApprovalReviewView.vue'),
    },
    {
      path: '/',
      component: DashLayout,
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          meta: { requiresAuth: true, title: 'Dashboard' },
          component: () => import('@/views/DashboardView.vue'),
        },
        {
          path: 'clients',
          name: 'clients',
          meta: { requiresAuth: true, title: 'Clients' },
          component: () => import('../views/clients/ClientsView.vue'),
        },
        {
          path: 'clients/:id/',
          name: 'client-detail',
          meta: { requiresAuth: true, title: 'Clients' },
          children:[
            {
              path:"",
              name: 'client-detail-view',
              meta: { title: 'Clients' },
              component: () => import('../views/clients/ClientsView.vue'),
            },
          ]

        },
        // "Projects" are called Campaigns in the product; route names stay the same so
        // existing `{ name: 'projects' }` links keep working, and old URLs redirect.
        {
          path: 'campaigns',
          name: 'projects',
          meta: { requiresAuth: true, title: 'Campaigns' },
          component: () => import('@/views/ProjectsView.vue'),
        },
        {
          path: 'campaigns/:id',
          name: 'project-detail',
          meta: { requiresAuth: true, title: 'Campaign' },
          component: () => import('@/views/ProjectDetailView.vue'),
        },
        { path: 'projects', redirect: '/campaigns' },
        { path: 'projects/:id', redirect: (to) => `/campaigns/${to.params.id}` },
        {
          path: 'team',
          name: 'team',
          meta: { requiresAuth: true, title: 'Team' },
          component: () => import('@/views/team/TeamView.vue'),
        },
        {
          path: 'settings',
          name: 'org-settings',
          meta: { requiresAuth: true, title: 'Settings' },
          component: () => import('@/views/settings/OrgSettingsView.vue'),
        },
        {
          path: 'platform-connections/:id',
          name: 'platform-connection-detail',
          meta: { requiresAuth: true, title: 'Platform Connection' },
          component: () => import('@/views/platformconnections/PlatformConnectionDetailView.vue'),
        },
        {
          path: 'notifications',
          name: 'notifications',
          meta: { requiresAuth: true, title: 'Notifications' },
          component: () => import('@/views/notifications/NotificationsView.vue'),
        },
        {
          path: 'billing',
          name: 'billing',
          meta: { requiresAuth: true, title: 'Billing' },
          component: () => import('@/views/billing/BillingView.vue'),
        },
        {
          path: 'profile',
          name: 'profile',
          meta: { requiresAuth: true, title: 'My Account' },
          component: () => import('@/views/profile/ProfileView.vue'),
        },
        {
          path: 'posts',
          name: 'posts',
          meta: { requiresAuth: true, title: 'Posts' },
          component: () => import('@/views/posts/PostsView.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      meta: { title: 'Page not found' },
      component: () => import('@/views/NotFoundView.vue'),
    },
  ]

  const router = _createRouter({
    history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory(),
    routes,
  })

  return router
}

export function setupRouterGuards(router: Router, pinia: Pinia) {
  router.beforeEach(async (to) => {
    const auth = useAuthStore(pinia)

    const needsHydration = auth.isAuthenticated && !auth.user

    if (needsHydration) {
      try {
        await auth.hydrateAuthContext()
      } catch (error) {
        console.error('Failed to hydrate auth context:', error)
        await auth.logout()
        return { path: '/auth/', query: { redirect: to.fullPath } }
      }
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    const isGuestRoute = to.matched.some((record) => record.meta.guest)

    if (requiresAuth && !auth.isAuthenticated) {
      return { path: '/auth/', query: { redirect: to.fullPath } }
    }

    if (isGuestRoute && auth.isAuthenticated) {
      const guestRedirect =
        typeof to.query.redirect === 'string' && to.query.redirect.startsWith('/')
          ? to.query.redirect
          : '/dashboard'
      return guestRedirect
    }
  })
}
